import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import PlantBackground from "components/Layouts/PlantBackground";
import Email from "components/common/Email";
import {
  showFailureNotification,
  showSuccessNotification,
} from "store/actions/notification";
import {
  cognitoConfirmForgotPassword,
  cognitoForgotPassword,
} from "services/cognitoAuth";

import styles from "./ConfirmResetPassword.module.css";

const ConfirmResetPassword = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const resendCode = async () => {
    if (!email.trim()) {
      dispatch(showFailureNotification("Enter your email to request a code"));
      return;
    }
    setResending(true);
    try {
      await cognitoForgotPassword(email.trim());
      dispatch(
        showSuccessNotification(
          "A new verification code has been sent to your email."
        )
      );
    } catch (e) {
      dispatch(
        showFailureNotification(
          e?.message || "Failed to send verification code"
        )
      );
    } finally {
      setResending(false);
    }
  };

  const submit = async () => {
    if (!email.trim()) {
      dispatch(showFailureNotification("Email is required"));
      return;
    }
    if (!code.trim()) {
      dispatch(showFailureNotification("Verification code is required"));
      return;
    }
    if (!password) {
      dispatch(showFailureNotification("Password is required"));
      return;
    }
    if (password !== confirm) {
      dispatch(showFailureNotification("Passwords do not match"));
      return;
    }
    setLoading(true);
    try {
      await cognitoConfirmForgotPassword(email.trim(), code.trim(), password);
      dispatch(showSuccessNotification("Password updated. Please sign in."));
      history.push("/login");
    } catch (e) {
      dispatch(
        showFailureNotification(e?.message || "Failed to reset password")
      );
    } finally {
      setLoading(false);
    }
  };

  const onKeyUp = (e) => {
    if (e.key === "Enter") submit();
  };

  return (
    <PlantBackground>
      <div className={styles.container} onKeyUp={onKeyUp}>
        <div className={styles.formContainer}>
          <label className={styles.label}>Confirm Reset</label>
          <p style={{ color: "#fff", marginBottom: 12, fontSize: 13 }}>
            Enter the verification code from your email along with a new
            password.
          </p>
          <div className={styles.form}>
            <Email
              change={(e) => setEmail(e.target.value.replace(/\s/g, ""))}
              email={email}
            />
            <input
              type="text"
              placeholder="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
              style={inputStyle}
            />
            <div style={resendRowStyle}>
              <button
                type="button"
                onClick={resendCode}
                disabled={resending || loading}
                style={linkButtonStyle}
              >
                {resending ? "Sending..." : "Request a new code"}
              </button>
            </div>
            <input
              type="password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              style={inputStyle}
            />
            <footer className={styles.footer}>
              <button
                onClick={submit}
                className={styles.submit}
                type="button"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Set new password"}
              </button>
            </footer>
          </div>
        </div>
      </div>
    </PlantBackground>
  );
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  marginTop: 10,
  borderRadius: 4,
  border: "1px solid #ccc",
  fontSize: 14,
};

const resendRowStyle = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 6,
};

const linkButtonStyle = {
  background: "none",
  border: "none",
  padding: 0,
  color: "#fff",
  textDecoration: "underline",
  fontSize: 13,
  cursor: "pointer",
};

export default ConfirmResetPassword;
